import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { signIn, resetPassword } from '../lib/firebase'
import { PasswordInput } from '../components/PasswordInput'

/**
 * /signin — email + password. Same visual language as the mobile
 * SignIn (white canvas, 52px pill inputs, navy filled CTA, soft
 * zoom-out on success). Migrated Heera users will land here on
 * first post-cutover visit; their AES password from Heera doesn't
 * transfer, so the "Forgot password?" link is the path back in.
 */
export function SignIn() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [success, setSuccess] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) return
    setSubmitting(true)
    setError(null)
    try {
      await signIn(email.trim().toLowerCase(), password)
      setSuccess(true)
      // Brief delay so the success state renders before nav — feels
      // less abrupt than instant redirect.
      setTimeout(() => navigate('/search', { replace: true }), 400)
    } catch (err) {
      setError(prettyAuthError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function onForgot() {
    if (!email) {
      setError('Enter your email above first.')
      return
    }
    try {
      await resetPassword(email.trim().toLowerCase())
      setResetSent(true)
    } catch (err) {
      setError(prettyAuthError(err))
    }
  }

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-6 relative z-[1] bg-vault-bg text-vault-cream overflow-hidden">
      {/* Vault atmosphere — same world as the landing, deeper in. */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(110% 85% at 50% 34%, rgba(10,15,30,0) 0%, rgba(5,7,12,0.55) 60%, #05070C 100%)' }}
        />
        <div className="vault-shaft absolute inset-0" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={success ? { scale: 0.96, opacity: 0 } : { scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-sm"
      >
        <Link to="/" className="block text-center font-vault text-4xl tracking-tight text-vault-cream mb-2">
          DiaEx
        </Link>
        <p className="text-center text-sm text-vault-text-2 mb-8">
          Welcome back. Sign in to continue.
        </p>

        <form onSubmit={onSubmit} className="vault-glass rounded-2xl p-6 sm:p-7 space-y-3">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="vault-input"
            required
            data-testid="signin-email"
          />
          <PasswordInput
            value={password}
            onChange={setPassword}
            required
            className="vault-input"
            testId="signin-password"
          />

          {error && (
            <p className="text-xs text-danger text-center" data-testid="signin-error">{error}</p>
          )}
          {resetSent && (
            <p className="text-xs text-success text-center">
              Check your email for a reset link.
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-[52px] inline-flex items-center justify-center rounded-full bg-vault-gold text-vault-void font-semibold text-base hover:bg-vault-gold-hover active:scale-[0.99] transition-all duration-default ease-premium disabled:opacity-50"
            data-testid="signin-submit"
          >
            {submitting ? 'Signing in…' : 'Log in'}
          </button>

          <div className="flex items-center justify-between pt-2 text-xs">
            <button
              type="button"
              onClick={onForgot}
              className="text-vault-text-3 hover:text-vault-cream transition-colors"
            >
              Forgot password?
            </button>
            <Link to="/register" className="text-vault-gold font-medium hover:text-vault-gold-hover">
              Create account →
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  )
}

function prettyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email or password is incorrect.'
    case 'auth/too-many-requests':
      return 'Too many tries. Wait a moment, then try again.'
    case 'auth/invalid-email':
      return 'That email address looks off.'
    case 'auth/user-disabled':
      return 'Your account is disabled. Email info@thediaex.com.'
    default:
      return 'Something went wrong. Try again or reset your password.'
  }
}
